export type PromotionDiscountType = "fixed" | "percentage";

export interface PromotionRecord {
  id: number;
  plan_id: number | null;
  plan_code?: string | null;
  plan_name?: string | null;
  code: string;
  name: string;
  description?: string | null;
  customer_id?: number | null;
  customer_code?: string | null;
  customer_name?: string | null;
  value: number;
  discount_type: PromotionDiscountType;
  valid_from?: string | null;
  valid_till?: string | null;
  valid_day?: string | null;
  status: number | boolean;
  plan_status?: number | boolean;
  applicable_for?: number | boolean;
  created_at?: string;
  updated_at?: string;
}

export interface ActivePromotion {
  id: number;
  plan_id: number | null;
  code: string;
  name: string;
  description?: string | null;
  customer_id?: number | null;
  customer_name?: string | null;
  value: number;
  discount_type: PromotionDiscountType;
  valid_from?: string | null;
  valid_till?: string | null;
  valid_day?: string | null;
  applied_amount: number;
  is_customer_specific: boolean;
}

export const WEEKDAY_OPTIONS = [
  { label: "Sun", value: "sunday" },
  { label: "Mon", value: "monday" },
  { label: "Tue", value: "tuesday" },
  { label: "Wed", value: "wednesday" },
  { label: "Thu", value: "thursday" },
  { label: "Fri", value: "friday" },
  { label: "Sat", value: "saturday" },
] as const;

const TRUTHY_VALUES = new Set(["1", "true", "yes", "on"]);
const WEEKDAY_LOOKUP: Record<string, string> = {
  sun: "sunday",
  sunday: "sunday",
  0: "sunday",
  mon: "monday",
  monday: "monday",
  1: "monday",
  tue: "tuesday",
  tues: "tuesday",
  tuesday: "tuesday",
  2: "tuesday",
  wed: "wednesday",
  wednesday: "wednesday",
  3: "wednesday",
  thu: "thursday",
  thur: "thursday",
  thurs: "thursday",
  thursday: "thursday",
  4: "thursday",
  fri: "friday",
  friday: "friday",
  5: "friday",
  sat: "saturday",
  saturday: "saturday",
  6: "saturday",
};

const WEEKDAY_LABELS: Record<string, string> = {
  sunday: "Sunday",
  monday: "Monday",
  tuesday: "Tuesday",
  wednesday: "Wednesday",
  thursday: "Thursday",
  friday: "Friday",
  saturday: "Saturday",
};

export const normalizePromotionDiscountType = (
  value?: string | null
): PromotionDiscountType =>
  String(value || "").trim().toLowerCase() === "percentage"
    ? "percentage"
    : "fixed";

export const isPromotionEnabled = (value?: boolean | number | string | null) =>
  TRUTHY_VALUES.has(String(value ?? 1).trim().toLowerCase());

export const normalizePromotionDay = (value?: string | null) => {
  const normalized = String(value || "").trim().toLowerCase();
  return WEEKDAY_LOOKUP[normalized] || "";
};

export const parsePromotionDays = (value?: string | null): string[] => {
  if (!value) return [];

  const normalizedDays = String(value)
    .split(/[,\s|/]+/)
    .map((day) => normalizePromotionDay(day))
    .filter(Boolean);

  return Array.from(new Set(normalizedDays));
};

export const serializePromotionDays = (days: string[]) =>
  Array.from(new Set(days.map((day) => normalizePromotionDay(day)).filter(Boolean))).join(",");

export const formatPromotionDays = (value?: string | null) => {
  const days = parsePromotionDays(value);
  if (!days.length) return "Every day";
  return days.map((day) => WEEKDAY_LABELS[day] || day).join(", ");
};

export const formatLocalDateKey = (value = new Date()) => {
  const year = value.getFullYear();
  const month = String(value.getMonth() + 1).padStart(2, "0");
  const day = String(value.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

export const getWeekdayKey = (value = new Date()) =>
  WEEKDAY_OPTIONS[value.getDay()]?.value || "sunday";

export const calculatePromotionAmount = (
  subtotal: number,
  promotion: Pick<PromotionRecord, "discount_type" | "value">
) => {
  const sanitizedSubtotal = Math.max(0, Number(subtotal || 0));
  const sanitizedValue = Math.max(0, Number(promotion.value || 0));

  if (normalizePromotionDiscountType(promotion.discount_type) === "percentage") {
    return Math.min(
      sanitizedSubtotal,
      sanitizedSubtotal * (Math.min(100, sanitizedValue) / 100)
    );
  }

  return Math.min(sanitizedSubtotal, sanitizedValue);
};

export const isPromotionEligibleForCustomer = (
  promotion: PromotionRecord,
  customerId?: number | null
) => {
  const promotionCustomerId = Number(promotion.customer_id || 0);
  if (!promotionCustomerId) {
    return true;
  }

  return Number(customerId || 0) === promotionCustomerId;
};

export const isPromotionActive = (
  promotion: PromotionRecord,
  customerId?: number | null,
  value = new Date()
) => {
  if (!isPromotionEnabled(promotion.status)) return false;
  if (!isPromotionEnabled(promotion.plan_status ?? promotion.status)) return false;
  if (!isPromotionEligibleForCustomer(promotion, customerId)) return false;

  const today = formatLocalDateKey(value);
  if (promotion.valid_from && promotion.valid_from > today) return false;
  if (promotion.valid_till && promotion.valid_till < today) return false;

  const validDays = parsePromotionDays(promotion.valid_day);
  if (validDays.length && !validDays.includes(getWeekdayKey(value))) {
    return false;
  }

  return true;
};

export const resolveBestPromotion = (
  promotions: PromotionRecord[],
  options: {
    customerId?: number | null;
    subtotal?: number;
    date?: Date;
  } = {}
): ActivePromotion | null => {
  const date = options.date || new Date();
  const subtotal = Math.max(0, Number(options.subtotal || 0));
  const customerId = options.customerId ?? null;

  const matches = promotions
    .filter((promotion) => isPromotionActive(promotion, customerId, date))
    .map((promotion) => ({
      ...promotion,
      applied_amount: calculatePromotionAmount(subtotal, promotion),
      is_customer_specific: Number(promotion.customer_id || 0) > 0,
    }));

  if (!matches.length) return null;

  matches.sort((left, right) => {
    if (right.applied_amount !== left.applied_amount) {
      return right.applied_amount - left.applied_amount;
    }

    if (Number(right.is_customer_specific) !== Number(left.is_customer_specific)) {
      return Number(right.is_customer_specific) - Number(left.is_customer_specific);
    }

    if (Number(right.value || 0) !== Number(left.value || 0)) {
      return Number(right.value || 0) - Number(left.value || 0);
    }

    return Number(right.id || 0) - Number(left.id || 0);
  });

  const best = matches[0];

  return {
    id: Number(best.id),
    plan_id: best.plan_id ?? null,
    code: best.code,
    name: best.name,
    description: best.description || "",
    customer_id: best.customer_id ?? null,
    customer_name: best.customer_name || null,
    value: Number(best.value || 0),
    discount_type: normalizePromotionDiscountType(best.discount_type),
    valid_from: best.valid_from || null,
    valid_till: best.valid_till || null,
    valid_day: best.valid_day || null,
    applied_amount: Number(best.applied_amount || 0),
    is_customer_specific: !!best.is_customer_specific,
  };
};

export const formatPromotionValue = (
  promotion: Pick<ActivePromotion | PromotionRecord, "discount_type" | "value">
) =>
  normalizePromotionDiscountType(promotion.discount_type) === "percentage"
    ? `${Number(promotion.value || 0)}% off`
    : `$${Number(promotion.value || 0).toFixed(2)} off`;

export const formatPromotionWindow = (
  promotion: Pick<PromotionRecord, "valid_from" | "valid_till">
) => {
  if (promotion.valid_from && promotion.valid_till) {
    return `${promotion.valid_from} to ${promotion.valid_till}`;
  }

  if (promotion.valid_from) {
    return `Starts ${promotion.valid_from}`;
  }

  if (promotion.valid_till) {
    return `Until ${promotion.valid_till}`;
  }

  return "No date limit";
};
